import { useState } from "react";
import {
  Button,
  Chip,
  Slider,
  SectionLabel,
  Tabs,
  Toggle,
  MixedOptionRow,
  DisabledOptionRow,
  DisabledSectionBody,
  DisabledSlider,
} from "../../kit";
import type { TabItem } from "../../kit";
import { useCaps } from "../../editor/CapsProvider";
import { useRing } from "../ringContext";
import { isSegmentSplit } from "../ratios";
import { grooveWidthMin, quarterGrooveSlider } from "../junctionUi";
import {
  SHOULDER_SLIDERS,
  addShoulder,
  customPaths,
  newShoulderGroup,
  plainGrooves,
  regenerateShoulder,
  removeShoulder,
  shoulderGroups,
  shoulderStartParams,
  sliderMax,
} from "../cnc/pathGrooves";
import type { ShoulderGroup } from "../cnc/pathGrooves";
import {
  V_ANGLE,
  addPartialStep,
  partialStepGroups,
  partialStepSliders,
  regeneratePartialStep,
  removePartialStep,
  takesVAngle,
  vDepthMM,
  withVAngle,
} from "./bodyUi";
import type { PartialStepGroup } from "./bodyUi";
import type {
  Catalogs, DesignGroove, GrooveDirection, GrooveSurface, JointType, RingState,
} from "../../../shared/api-contract";


/** Shown when the connected viewer predates v37 so the catalog is absent. */
const JOINT_FALLBACK_LABELS = ["Classic", "V 60°", "V 120°", "U Round", "Convex U", "Square"];

/** `undefined` jointType is the legacy parabolic cut — a real option, so it
 * needs an id in the chip row. Never sent over the wire as a JointType. */
const CLASSIC = "classic";

/** The cross-section row, shared by the joint grooves and every Design groove. */
function jointOptions(catalogs: Catalogs) {
  return [
    { id: CLASSIC, label: "Classic" },
    ...(catalogs.jointTypes ?? []).map((j) => ({ id: j.id, label: j.label })),
  ];
}

/** Bead joints sweep metal instead of carving (`isBeadJoint` in the engine). */
const isBeadJoint = (t: JointType | undefined) => t === "milgrain" || t === "milgrain2";

/**
 * Does a metal boundary show on a surface the seam groove could be cut into?
 * Mirrors the engine's `seamAxis`: two metals, and either a circumferential
 * boundary (a lathe-Y line) or an axial one (a line across the width). An AXIAL
 * division with a segment pattern is the one combination with neither — that
 * boundary is buried inside the metal, and the engine carves nothing.
 */
const seamCarvable = (state: RingState) =>
  state.materials.length > 1 &&
  (state.division !== "axial" || !isSegmentSplit(state.splitPatternId));

/** v41 Design grooves. Small fixed lists — no catalog round-trip. A v66
 * "path" groove is not on this row: switching one to Around or Across would
 * drop its path, so path grooves get their own rows below the chips. */
const GROOVE_DIRECTIONS: { id: Exclude<GrooveDirection, "path">; label: string }[] = [
  { id: "horizontal", label: "Around" },
  { id: "vertical", label: "Across" },
];

const GROOVE_SURFACES: { id: GrooveSurface; label: string }[] = [
  { id: "polished", label: "Polished" },
  { id: "matt", label: "Matt" },
  { id: "sand", label: "Sand" },
];



/** The tab's three groups. All three already lived here, stacked; the strip
 * only says where one ends and the next begins. */
const SUB_TABS: TabItem[] = [
  { id: "separation", label: "Separation", icon: "join_inner" },
  { id: "design", label: "Design", icon: "architecture" },
  { id: "edges", label: "Edges", icon: "line_weight" },
];

/**
 * Joints tab — the SEAM between metals and the decorative grooves cut into
 * the band, behind a three-way sub-tab
 * strip (Separation · Design · Edges).
 *
 * Separation is the seam between metals; Design is the channel the stones sit
 * in plus the free-placed decorative grooves; Edges is the two band edges. The
 * whole tab used to be one 20-control scroll — nothing moved out of it and no
 * control changed, they are just grouped by what they cut.
 */
export function JointsTab() {
  const caps = useCaps();
  const [sub, setSub] = useState<string>(SUB_TABS[0].id);

  return (
    <div className="flex flex-col gap-5">
      {/* Its own layoutId: the RingBuilder's TabRail indicator must not fly
          down here when a sub-tab is picked. */}
      <Tabs tabs={SUB_TABS} active={sub} onSelect={setSub} layoutId="joints-sub-active" className="-mx-6 px-3" />

      {sub === "separation" ? <SeparationPanel /> : null}
      {sub === "design" ? <DesignPanel /> : null}
      {sub === "edges" ? <EdgesPanel /> : null}

      {!caps.ringBuilderV37 ? (
        <span className="font-body text-[10px] text-outline">
          Joint grooves need viewer contract v37 — this viewer reports v{caps.version}.
        </span>
      ) : null}
    </div>
  );
}

/** Separation — the seam between two metals, and which seams get carved. */
function SeparationPanel() {
  const caps = useCaps();
  return (
    <div className="flex flex-col gap-5">
      {caps.ringBuilderV37 ? (
        <>
          <JointGrooves />
          {caps.ringGeometryV38 ? (
            <Boundaries />
          ) : (
            <span className="font-body text-[10px] text-outline">
              Per-boundary seam grooves need viewer contract v38.
            </span>
          )}
        </>
      ) : (
        <>
          <SectionLabel>Joint Grooves</SectionLabel>
          <DisabledSectionBody>
            <DisabledOptionRow label="Joint Cross-Section" options={JOINT_FALLBACK_LABELS} />
            <DisabledSlider label="Joint Width" value="0.30 mm" />
            <DisabledSlider label="Joint Depth" value="0.10 mm" />
          </DisabledSectionBody>
        </>
      )}
    </div>
  );
}

/** Design — the channel the stones are seated in, and the free decorative
 * grooves placed anywhere on the band. */
function DesignPanel() {
  const caps = useCaps();

  // ITEM 17 — the Channel / Groove Cut rows moved to the Gems tab, beside the
  // stones that sit in the trench and next to the Setting Type row that is the
  // only thing that turns it on. What lived here was two tabs away from that
  // decision, and four of its six sliders were re-derived by `setDiamond` on
  // every neighbouring click anyway (see `ChannelCutRows` in GemsTab).
  return (
    <div className="flex flex-col gap-5">
      {caps.ringLongTailV41 ? <DesignGrooves /> : null}
      {caps.ringPathGroovesV66 ? <PathGrooves /> : null}
    </div>
  );
}

/** Edges — the two band edges: the groove that runs along them and the v38
 * per-side treatments. */
function EdgesPanel() {
  // ITEM 19 — Edge Grooves and Per-Side Edges moved to the Profile tab, under
  // the Edge Treatment row they were always describing. An edge is a profile
  // decision; it only lived here because "everything carved into the metal"
  // was the tab's original organising idea.
  return (
    <span className="font-body text-[10px] text-outline">
      Edge grooves and per-side edges now live in the Profile tab, under Edge Treatment.
    </span>
  );
}

/** v37 `setJointGrooves` — cross-section + seam/edge placement. All three write
 * the one `GrooveConfig` the Profile tab's decorative grooves also read. */
function JointGrooves() {
  const { state, catalogs, r, apply } = useRing();
  const caps = useCaps();
  const g = state.grooves;

  const carvable = seamCarvable(state);
  // The reference has no seam on/off switch in this panel — a Joint Type is
  // always selected and the groove is live. Picking one here is therefore what
  // turns the seam carve on; the Boundaries rows below turn it back off.
  const selectJoint = (id: string) =>
    void apply(r.setJointGrooves({
      jointType: id === CLASSIC ? undefined : (id as JointType),
      ...(carvable && !g.seam ? { seam: true } : {}),
    }));

  // The two milgrain joints add beads instead of removing metal, so "depth" is
  // meaningless for them and "width" becomes the bead size.
  const beaded = isBeadJoint(g.jointType);
  const jointWidth = quarterGrooveSlider(caps);

  return (
    <>
      <SectionLabel>Joint Grooves</SectionLabel>
      <MixedOptionRow
        label="Cross-Section"
        options={jointOptions(catalogs)}
        selected={g.jointType ?? CLASSIC}
        onSelect={selectJoint}
      />
      <span className="font-body text-[10px] text-outline">
        Shapes every carved groove — decorative, seam and edge alike. Classic keeps the legacy cut.
      </span>
      {/* Only worth saying for a multi-metal band: with one metal there is
          visibly no seam, but a buried boundary needs explaining. */}
      {!carvable && state.materials.length > 1 ? (
        <span className="font-body text-[10px] text-outline">
          This split's boundary is buried inside the metal (an axial segment split), so there is no
          seam line to carve — change the division in the Material tab.
        </span>
      ) : null}

      {/* Ranges and tick counts are the reference's measured tick sets:
          Width 0.25…3.00 step 0.25 (12 ticks), Depth 0.10…1.00 (10). The engine
          clamps to the same bounds, so neither end is reachable-but-ignored.
          v67 lowers the engine's width floor to 0.15 mm: on that viewer the
          track starts at 0.15 and steps 0.05, the 12 ruler marks stay put. */}
      <Slider label="Joint Width" min={jointWidth.min} max={3} step={jointWidth.step} ticks={jointWidth.ticks}
        value={g.widthMM} unit="mm"
        onChange={(v) => void r.setJointGrooves({ widthMM: v })} />
      <Slider label="Joint Depth" min={0.1} max={1} step={0.05} ticks={10} value={g.depthMM} unit="mm"
        disabled={beaded}
        onChange={(v) => void r.setJointGrooves({ depthMM: v })} />
      <span className="font-body text-[10px] text-outline">
        {beaded
          ? "Milgrain adds beads instead of carving — width sets the bead size (half of it); depth does not apply."
          : "One size for every carved groove — the decorative grooves and the edge grooves in the Profile tab included. Edge grooves cut shallower by design."}
      </span>

      {/* ITEM 18 — "Why Surface in joints? when we have them in Materials".
          Because it is a different thing wearing the same word: this is the
          finish on the CUT FLOOR, while the Material tab's Surface is the
          metal's own finish. RENAMED, never deleted — the row is the only way
          to reach `grooveSurfaceLayers`, and deleting it would orphan the
          engine feature the v43 wave added. `disabled` on a bead joint matches
          the Joint Depth slider two rows up: beads add metal instead of
          cutting, so there is no floor to finish. */}
      {caps.ringMilgrainV51 && beaded ? <MilgrainRows /> : null}

      <MixedOptionRow
        label="Groove Floor"
        options={GROOVE_SURFACES}
        selected={g.surface ?? "polished"}
        disabled={beaded}
        onSelect={(id) => void apply(r.setJointGrooves({ surface: id }))}
      />
      <span className="-mt-2 font-body text-[10px] text-outline">
        {beaded
          ? "Bead joints add metal instead of cutting, so there is no groove floor to finish."
          : "The finish on the cut floor itself — not the band's metal finish, which lives in the Material tab. Matt or Sand gives every carved groove its own material."}
      </span>
    </>
  );
}


/**
 * v41 "Design" — the arbitrary-length list of decorative grooves that sits
 * beside the single global `grooves` config. Numbered chips add/select/remove,
 * exactly as the free-stone editor does, and one panel edits the selection.
 *
 * Every groove feeds the same max-depth outer carve as the joint grooves, so
 * two overlapping grooves cut to the deeper of the pair, not to the sum.
 *
 * The chips number the Around/Across grooves only. v66 path grooves share the
 * list but are edited by `PathGrooves` below — as a shoulder, or as a compact
 * custom row — never through this panel's Direction row, which would drop
 * their path.
 */
function DesignGrooves() {
  const caps = useCaps();
  const { state, catalogs, r, apply } = useRing();
  const list = plainGrooves(state.designGrooves ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Falling back to the last groove means the selection can never dangle after
  // a removal — no cleanup effect, and the panel is never empty for no reason.
  const selected = list.find((gr) => gr.id === selectedId) ?? list[list.length - 1] ?? null;

  const add = async () => {
    const before = new Set((state.designGrooves ?? []).map((gr) => gr.id));
    const next = await r.addDesignGroove();
    if (!next) return;
    await apply(Promise.resolve(next));
    setSelectedId(next.designGrooves?.find((gr) => !before.has(gr.id))?.id ?? null);
  };

  const vertical = selected?.direction === "vertical";
  const beaded = isBeadJoint(selected?.jointType);
  // Vertical position is an arc length at the OUTER radius (the engine divides
  // by `maxR`), so the useful range is the outside circumference.
  const outerCircMM = Math.PI * ((state.spec?.innerDiameterMM ?? 17.35) + 2 * state.thicknessMM);
  const halfWidthMM = state.widthMM / 2;
  const offBand = !!selected && !vertical && Math.abs(selected.positionMM) > halfWidthMM;

  return (
    <>
      <div className="flex items-baseline justify-between">
        <SectionLabel>Design Grooves</SectionLabel>
        <span className="font-body text-[10px] tabular-nums text-outline">
          {list.length ? `${list.length} placed` : "None"}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        {list.map((gr, i) => (
          <Chip key={gr.id} selected={gr.id === selected?.id} onClick={() => setSelectedId(gr.id)}>
            {i + 1}
          </Chip>
        ))}
        <Chip selected={false} onClick={() => void add()}>+ Add</Chip>
      </div>

      {selected ? (
        <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
          <div className="flex items-baseline justify-between">
            <SectionLabel>Groove {list.indexOf(selected) + 1}</SectionLabel>
            <span className="font-body text-[10px] tabular-nums text-outline">
              {list.indexOf(selected) + 1} of {list.length}
            </span>
          </div>

          <MixedOptionRow
            label="Direction"
            options={GROOVE_DIRECTIONS}
            selected={selected.direction ?? "horizontal"}
            onSelect={(id) => void apply(r.updateDesignGroove(selected.id, { direction: id }))}
          />

          <Slider
            label={vertical ? "Position Around" : "Position Across"}
            min={vertical ? 0 : -halfWidthMM}
            max={vertical ? outerCircMM : halfWidthMM}
            step={0.05}
            unit="mm"
            value={selected.positionMM}
            onChange={(v) => void r.updateDesignGroove(selected.id, { positionMM: v })}
          />
          <span className="-mt-2 font-body text-[10px] text-outline">
            {/* The engine measures from 3 o'clock (stone-frame θ = 0), not from
                the top: see `DesignGroove.positionMM` in the contract. The
                label says so rather than the engine moving every saved groove. */}
            {vertical
              ? `Measured around the outside of the band from 3 o'clock — the right-hand side, with the top of the ring at 12 — on toward 6 o'clock. The top of the ring is at about ${(outerCircMM * 0.75).toFixed(1)} mm.`
              : offBand
                ? "Currently off the band — this groove carves nothing until the width grows or you move it back."
                : "Distance from the band's centre line."}
          </span>

          <MixedOptionRow
            label="Joint Type"
            options={jointOptions(catalogs)}
            selected={selected.jointType ?? CLASSIC}
            onSelect={(id) =>
              void apply(
                r.updateDesignGroove(selected.id, {
                  jointType: id === CLASSIC ? undefined : (id as JointType),
                }),
              )
            }
          />

          <Slider label="Width" min={grooveWidthMin(caps)} max={3} step={0.05} unit="mm" value={selected.widthMM}
            onChange={(v) => void r.updateDesignGroove(selected.id, { widthMM: v })} />
          <Slider label="Depth" min={0.1} max={1} step={0.05} unit="mm" value={selected.depthMM}
            disabled={beaded || selected.vAngleDeg !== undefined}
            onChange={(v) => void r.updateDesignGroove(selected.id, { depthMM: v })} />

          {caps.ringBodyV67 && takesVAngle(selected) ? <VAngleRows groove={selected} /> : null}

          {/* Same rename as the Separation panel's row (item 18): the finish
              on this groove's own floor, not the band's metal finish. */}
          <MixedOptionRow
            label="Groove Floor"
            options={GROOVE_SURFACES}
            selected={selected.surface ?? "polished"}
            disabled={beaded}
            onSelect={(id) => void apply(r.updateDesignGroove(selected.id, { surface: id }))}
          />

          {beaded ? (
            <span className="font-body text-[10px] text-outline">
              {vertical
                ? "Milgrain beads only run around the band — switch this groove to Around, or pick another joint type."
                : "Milgrain adds beads instead of carving — width sets the bead size; depth does not apply."}
            </span>
          ) : null}

          <Button variant="outline" full onClick={() => void apply(r.removeDesignGroove(selected.id))}>
            Remove Groove
          </Button>
        </div>
      ) : (
        <span className="font-body text-[10px] text-outline">
          Extra decorative grooves, each placed and shaped on its own. Overlapping grooves cut to the
          deeper of the two, never deeper than both.
        </span>
      )}
    </>
  );
}

/**
 * v66 path grooves: the bypass stepped shoulder (the shop sample's end-mill
 * motif, a preset of five grooves mirrored either side of the design centre)
 * and any path groove no preset made.
 *
 * A shoulder is edited only through its seven params: every slider rebuilds
 * the whole group with the contract's `bypassShoulderGrooves` and sends the
 * list with `setDesignGrooves`. Sliders batch through the kit's draft
 * controller like every other row here; buttons store the returned state.
 *
 * Gated on `caps.ringPathGroovesV66`: an older viewer would carve a path groove
 * as a centre-line groove all the way round.
 */
function PathGrooves() {
  const caps = useCaps();
  const { state, r, apply } = useRing();
  const list = state.designGrooves ?? [];
  const groups = shoulderGroups(list);
  const custom = customPaths(list);

  const addOne = () => void apply(r.setDesignGrooves(addShoulder(list, shoulderStartParams(state.widthMM), newShoulderGroup(list))));

  return (
    <>
      <div className="flex items-baseline justify-between">
        <SectionLabel>Stepped Shoulders</SectionLabel>
        <span className="font-body text-[10px] tabular-nums text-outline">
          {groups.length ? `${groups.length} placed` : "None"}
        </span>
      </div>
      <span className="-mt-3 font-body text-[10px] text-outline">
        A diagonal step across the band with a groove running round the ring from each side of it, cut
        either side of the design centre — the bypass shoulder from the shop&rsquo;s sample program.
      </span>

      {groups.map((g, i) => (
        <ShoulderCard key={g.group} index={i} group={g} list={list} />
      ))}

      <Button variant="outline" full onClick={addOne}>
        Add Shoulder (Bypass)
      </Button>

      {caps.ringBodyV67 ? <PartialSteps /> : null}

      {custom.length ? (
        <>
          <SectionLabel>Custom Paths</SectionLabel>
          {custom.map((g) => (
            <CustomPathRow key={g.id} groove={g} />
          ))}
        </>
      ) : null}
    </>
  );
}

/**
 * v67 (band designs W3) — a TRUE V of a chosen included angle. On, the engine
 * derives the depth from the width (depth = (w/2) / tan(angle/2)) and cuts a
 * straight-sided V, so the Depth slider above is the angle's and is shown
 * dimmed. Off removes the key: the groove keeps the depth the angle gave it.
 */
export function VAngleRows({ groove }: { groove: DesignGroove }) {
  const { state, r, apply } = useRing();
  const list = state.designGrooves ?? [];
  const on = groove.vAngleDeg !== undefined;
  const deg = groove.vAngleDeg ?? V_ANGLE.start;
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex flex-col">
          <SectionLabel>True V</SectionLabel>
          <span className="font-body text-[10px] text-outline">
            {on ? `A ${Math.round(deg)}° V cuts ${vDepthMM(groove.widthMM, deg).toFixed(2)} mm deep at this width.` : "Cut a straight-sided V of a chosen angle."}
          </span>
        </span>
        <Toggle
          checked={on}
          label="True V angle"
          onChange={(v) => void apply(r.setDesignGrooves(withVAngle(list, groove.id, v ? V_ANGLE.start : null)))}
        />
      </div>
      {on ? (
        <Slider label="V Angle" min={V_ANGLE.min} max={V_ANGLE.max} step={V_ANGLE.step} unit="°" value={deg}
          minLabel="Sharp" maxLabel="Open"
          onChange={(v) => void r.setDesignGrooves(withVAngle(list, groove.id, v))} />
      ) : null}
    </>
  );
}

/**
 * v67 (band designs W3) — a step that stops part way round: the face is
 * lowered on one side of a line, over a stretch of the ring, and the line
 * turns and runs out through the edge at each end. One path groove made by
 * the contract's `partialStepGrooves` (the engine carves it as a POCKET that
 * stones are set into); every slider regenerates the group, like a shoulder.
 * A step all the way round is the parametric profile's (Profile tab).
 */
export function PartialSteps() {
  const { state, r, apply } = useRing();
  const list = state.designGrooves ?? [];
  const groups = partialStepGroups(list);
  return (
    <>
      <div className="flex items-baseline justify-between">
        <SectionLabel>Partial Steps</SectionLabel>
        <span className="font-body text-[10px] tabular-nums text-outline">
          {groups.length ? `${groups.length} placed` : "None"}
        </span>
      </div>
      <span className="-mt-3 font-body text-[10px] text-outline">
        One side of the face lowered over part of the ring, the step line turning out through the
        edge at each end. Stones placed on the lowered stretch sit on its floor.
      </span>
      {groups.map((g, i) => (
        <PartialStepCard key={g.group} index={i} group={g} />
      ))}
      <Button variant="outline" full onClick={() => void apply(r.setDesignGrooves(addPartialStep(list, state.widthMM)))}>
        Add Partial Step
      </Button>
    </>
  );
}

function PartialStepCard({ index, group }: { index: number; group: PartialStepGroup }) {
  const { state, r, apply } = useRing();
  const list = state.designGrooves ?? [];
  // The step reaches across THIS band: its span follows the width slider.
  const p = { ...group.params, spanMM: state.widthMM };
  const send = (next: typeof p) => r.setDesignGrooves(regeneratePartialStep(list, group.group, next));
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      <SectionLabel>Partial Step {index + 1}</SectionLabel>
      <MixedOptionRow
        label="Lowered Side"
        options={[{ id: "-y", label: "Left" }, { id: "+y", label: "Right" }]}
        selected={p.side}
        onSelect={(id) => void apply(send({ ...p, side: id as "+y" | "-y" }))}
      />
      {partialStepSliders(state.widthMM).map((sl) => (
        <div key={sl.key} className="flex flex-col gap-1">
          <Slider label={sl.label} min={sl.min} max={sl.max} step={sl.step} unit={sl.unit}
            value={Math.min(sl.max, Math.max(sl.min, p[sl.key]))}
            onChange={(v) => void send({ ...p, [sl.key]: v })} />
          {sl.hint ? <span className="font-body text-[10px] text-outline">{sl.hint}</span> : null}
        </div>
      ))}
      <Button variant="outline" full onClick={() => void apply(r.setDesignGrooves(removePartialStep(list, group.group)))}>
        Remove Partial Step
      </Button>
    </div>
  );
}

function ShoulderCard({ index, group, list }: { index: number; group: ShoulderGroup; list: DesignGroove[] }) {
  const { r, apply } = useRing();
  const p = group.params;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      <div className="flex items-baseline justify-between">
        <SectionLabel>Shoulder {index + 1}</SectionLabel>
        <span className="font-body text-[10px] tabular-nums text-outline">
          {group.ids.length} grooves, both sides
        </span>
      </div>
      {SHOULDER_SLIDERS.map((sl) => (
        <div key={sl.key} className="flex flex-col gap-1">
          <Slider
            label={sl.label}
            min={sl.min}
            max={sliderMax(sl, p)}
            step={sl.step}
            unit={sl.unit}
            value={p[sl.key]}
            onChange={(v) => void r.setDesignGrooves(regenerateShoulder(list, group.group, { ...p, [sl.key]: v }))}
          />
          {sl.hint ? <span className="font-body text-[10px] text-outline">{sl.hint}</span> : null}
        </div>
      ))}
      <Button variant="outline" full onClick={() => void apply(r.setDesignGrooves(removeShoulder(list, group.group)))}>
        Remove Shoulder
      </Button>
    </div>
  );
}

/** A path groove no preset made (from a saved design or another host): size and delete only. */
function CustomPathRow({ groove }: { groove: DesignGroove }) {
  const { r, apply } = useRing();
  const caps = useCaps();
  const points = groove.path?.points.length ?? 0;
  // The engine carves nothing for a path groove without a path or with a bead
  // joint; sliders there would be dead, so the row says why instead.
  const why = !points
    ? "It has no path, so it carves nothing."
    : isBeadJoint(groove.jointType)
      ? "Beads only run around the band, so this path carves nothing."
      : null;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-body text-[11px] text-on-surface-variant">
          Custom path{points ? `, ${points} points${groove.path?.mirror === "point" ? ", mirrored" : ""}` : ""}
        </span>
        <button
          type="button"
          onClick={() => void apply(r.removeDesignGroove(groove.id))}
          className="font-body text-[11px] text-primary underline underline-offset-2"
        >
          Delete
        </button>
      </div>
      {why ? (
        <span className="font-body text-[10px] text-outline">{why}</span>
      ) : (
        <>
          <Slider label="Width" min={grooveWidthMin(caps)} max={3} step={0.05} unit="mm" value={groove.widthMM}
            onChange={(v) => void r.updateDesignGroove(groove.id, { widthMM: v })} />
          <Slider label="Depth" min={0.1} max={1} step={0.05} unit="mm" value={groove.depthMM}
            onChange={(v) => void r.updateDesignGroove(groove.id, { depthMM: v })} />
        </>
      )}
    </div>
  );
}

/**
 * v38 "Boundaries" — which partition seams actually get a groove. Present
 * whenever a seam could be carved at all, as in the reference, and it is also
 * that carve's only switch: `grooves.seam` follows whether any boundary is on,
 * so there is no master toggle to find first.
 */
function Boundaries() {
  const { state, r, apply } = useRing();
  const g = state.grooves;
  const count = Math.max(0, state.materials.length - 1);
  if (!seamCarvable(state)) return null;

  const on = (i: number) => !!g.seam && g.seamBoundaries?.[i] !== false;
  const set = (i: number, v: boolean) => {
    const next = Array.from({ length: count }, (_, k) => (k === i ? v : on(k)));
    void apply(r.setJointGrooves({ seam: next.some(Boolean), seamBoundaries: next }));
  };
  const live = Array.from({ length: count }, (_, i) => on(i)).filter(Boolean).length;

  return (
    <>
      <div className="flex items-baseline justify-between">
        <SectionLabel>Boundaries</SectionLabel>
        <span className="font-body text-[10px] tabular-nums text-outline">{live}/{count}</span>
      </div>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center justify-between gap-2">
          <span className="font-body text-[11px] text-on-surface-variant">
            Disc {i + 1}–{i + 2}
          </span>
          <Toggle checked={on(i)} onChange={(v) => set(i, v)} label={`Boundary ${i + 1}`} />
        </div>
      ))}
    </>
  );
}

/**
 * ITEM 4 — the milgrain bead law, made adjustable.
 *
 * "Migrain is not working... option for Milgrain and Double Milgrain needed with
 * option to make them smaller and bigger and spacing and all needed (Advance
 * Options like Depth, size, and all of them needed)".
 *
 * Rows are a NUMBER now, per your call, rather than the "×2" joint-type enum
 * that could only ever say one or two — three rows were unreachable. Size,
 * spacing, depth and tessellation had no path at all. And the bead-size slider
 * that DID exist was the shared groove WIDTH, whose bead law clamps at 0.60 mm,
 * so the top 60% of that 0.25…3.00 track moved every carved groove on the ring
 * and left the beads exactly where they were.
 *
 * Only shown for a bead joint, and only on a viewer that speaks v51 —
 * `setMilgrain` is a new method name and RPC has no timeout.
 */
function MilgrainRows() {
  const { state, r, apply } = useRing();
  const m = state.milgrain ?? {};
  const rows = m.rows ?? (state.grooves.jointType === "milgrain2" ? 2 : 1);

  return (
    <>
      <div className="flex flex-col gap-2">
        <SectionLabel>Bead Rows</SectionLabel>
        <div className="flex gap-2">
          {[1, 2, 3].map((n) => (
            <Chip
              key={n}
              selected={rows === n}
              onClick={() => void apply(r.setMilgrain({ rows: n }))}
              className="flex-1"
            >
              {n}
            </Chip>
          ))}
        </div>
        <span className="font-body text-[10px] text-outline">
          One rail of beads, or two and three side by side. Replaces the old “×2” joint type,
          which could not reach three.
        </span>
      </div>

      <Slider
        label="Bead Size"
        min={0.1}
        max={1.5}
        step={0.05}
        unit="mm"
        value={m.beadSizeMM ?? Math.min(0.6, Math.max(0.25, (state.grooves.widthMM ?? 0.3) * 0.5))}
        onChange={(v) => void r.setMilgrain({ beadSizeMM: v })}
      />
      <Slider
        label="Bead Spacing"
        min={0.3}
        max={2}
        step={0.05}
        value={m.spacing ?? 0.55}
        disabled={rows < 2}
        onChange={(v) => void r.setMilgrain({ spacing: v })}
      />
      <Slider
        label="Bead Depth"
        min={0}
        max={0.9}
        step={0.05}
        value={m.sink ?? 0.45}
        onChange={(v) => void r.setMilgrain({ sink: v })}
      />
      <span className="font-body text-[10px] text-outline">
        Depth is how far each bead is seated INTO the metal — 0 leaves it sitting on the
        surface, 0.9 buries it almost to its equator.
      </span>

      <MixedOptionRow
        label="Bead Detail"
        options={[
          { id: "standard", label: "Standard" },
          { id: "fine", label: "Fine" },
        ]}
        selected={m.quality ?? "standard"}
        onSelect={(id) => void apply(r.setMilgrain({ quality: id as "standard" | "fine" }))}
      />
    </>
  );
}
